import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import { invalidateCachedFetch, mutateCachedFetch } from '@structural-climbing/auth'
import { Button, addToast } from '@structural-climbing/ui'
import type { GymContext } from '../layouts/GymLayout'
import { AnimatedNumber } from '../design/AnimatedNumber'
import { SkeletonCard } from '../design/Skeleton'
import { useMyProfile, type MyProfile } from '../features/profile/useMyProfile'
import { IDEAS_KEY, useIdeas, type IdeaKind } from '../features/ideas/useIdeas'
import { campaignProgress, daysLeft, markIdeasVisited } from '../features/ideas/campaign'
import { IdeaStatusChip } from '../features/ideas/IdeaStatusChip'
import { IdeaComposerSheet } from '../features/ideas/IdeaComposerSheet'
import { IdeaDetailSheet } from '../features/ideas/IdeaDetailSheet'
import { TrophyIcon } from '../features/ideas/IdeasTrophy'

const SPARKS = ['routes', 'training', 'friends', 'desk', 'progress'] as const

function BackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path d="m15 5-7 7 7 7" />
    </svg>
  )
}
function SendIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M4.5 12 20 4.5 15 20l-3.2-6.8z" />
      <path d="m11.8 13.2 3.4-3.4" />
    </svg>
  )
}
function KindIcon({ kind }: { kind: IdeaKind }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[18px] w-[18px]"
      aria-hidden="true"
    >
      {kind === 'idea' ? (
        <>
          <path d="M9.5 18h5M10.5 21h3" />
          <path d="M12 3a6 6 0 0 0-3.6 10.8c.7.6 1.1 1.3 1.1 2.1v.1h5v-.1c0-.8.4-1.5 1.1-2.1A6 6 0 0 0 12 3z" />
        </>
      ) : (
        <path d="M20.5 11.5a8 8 0 0 1-11.7 7.1L3.5 20l1.4-4.8a8 8 0 1 1 15.6-3.7z" />
      )}
    </svg>
  )
}

/** Climbing holds, drawn faintly behind the numbers. */
function Holds() {
  return (
    <svg
      viewBox="0 0 200 200"
      aria-hidden="true"
      className="pointer-events-none absolute -right-8 -top-6 h-48 w-48 opacity-20"
    >
      <g fill="var(--gym-primary)">
        <ellipse cx="40" cy="40" rx="18" ry="12" transform="rotate(-20 40 40)" />
        <ellipse cx="120" cy="30" rx="11" ry="9" />
        <path d="M150 80c14-6 30 2 30 14s-16 18-30 12-14-20 0-26z" />
        <ellipse cx="80" cy="95" rx="15" ry="10" transform="rotate(30 80 95)" />
        <circle cx="170" cy="150" r="12" />
        <ellipse cx="110" cy="150" rx="20" ry="11" transform="rotate(-35 110 150)" />
        <circle cx="35" cy="130" r="8" />
      </g>
    </svg>
  )
}

/**
 * "Top 5 din noiembrie": members send ideas, follow what happens to each one,
 * and on December 1 see the five being built.
 *
 * Opens over Home with its own back button. Its data is read here and only
 * here: two requests while the campaign runs, three after.
 */
export function IdeasPage() {
  const { gymId, gymSlug } = useOutletContext<GymContext>()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const reduced = useReducedMotion()
  const { data, loading, error, send, markSeen } = useIdeas()
  const { profile } = useMyProfile()

  const [composer, setComposer] = useState<{ open: boolean; text: string }>({
    open: false,
    text: '',
  })
  // The open idea by id, read from the latest data: a status that lands while
  // the sheet is open shows in it, rather than the copy it was opened with.
  const [detailId, setDetailId] = useState<string | null>(null)
  const detail = data?.mine.find((i) => i.id === detailId) ?? null
  const [freshId, setFreshId] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  // The status change that brought the dot back, opened once fresh data is here.
  const pendingOnArrival = useRef(false)
  // The data on screen when we learned it was out of date. Anything older than
  // the refetch must not be opened: it still shows the previous status.
  const staleData = useRef<typeof data | undefined>(undefined)
  const dataRef = useRef(data)
  useEffect(() => {
    dataRef.current = data
  }, [data])

  const formatDate = useMemo(() => {
    const f = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' })
    return (iso: string) => f.format(new Date(iso))
  }, [i18n.language])

  // Opening the page is the visit that clears the trophy's dot.
  useEffect(() => {
    markIdeasVisited()
  }, [])
  useEffect(() => {
    if (!profile?.ideasUpdatePending) return
    pendingOnArrival.current = true
    // The cached list predates the change. Read it again before showing it.
    staleData.current = dataRef.current
    invalidateCachedFetch(IDEAS_KEY)
    mutateCachedFetch<MyProfile>('my-profile', (p) => ({ ...p, ideasUpdatePending: false }))
    void markSeen()
  }, [profile?.ideasUpdatePending, markSeen])
  useEffect(() => {
    if (!pendingOnArrival.current || !data || data === staleData.current) return
    pendingOnArrival.current = false
    const changed = data.mine
      .filter((i) => i.status !== 'new')
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]
    if (changed) setTimeout(() => setDetailId(changed.id), reduced ? 0 : 600)
  }, [data, reduced])

  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) void navigate(-1)
    else void navigate(`/${gymSlug}/dashboard`, { replace: true })
  }

  const handleSend = useCallback(
    async (kind: IdeaKind, body: string) => {
      const result = await send(kind, body, gymId)
      if (result === 'closed' || result === 'failed') {
        addToast({
          title: t(result === 'closed' ? 'ideas.closed' : 'ideas.sendFailed'),
          color: 'danger',
        })
        return false
      }
      setFreshId(result.id)
      addToast({
        title: t(kind === 'idea' ? 'ideas.sentIdea' : 'ideas.sentFeedback'),
        color: 'success',
      })
      setTimeout(
        () =>
          listRef.current?.scrollIntoView({
            behavior: reduced ? 'auto' : 'smooth',
            block: 'start',
          }),
        350,
      )
      return true
    },
    [send, gymId, t, reduced],
  )

  const open = data?.campaign.open ?? true
  const left = data ? daysLeft(data.campaign.closesAt) : 0
  const progress = data ? (open ? campaignProgress(data.campaign.closesAt) * 0.66 : 1) : 0
  const mineIdeaIds = new Set(data?.mine.map((i) => i.id))

  return (
    <div className="min-h-full bg-[var(--gym-page)] pt-[var(--safe-t)]">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-5 px-4 pb-10">
        <header className="flex items-center gap-2 pt-3">
          <Button
            isIconOnly
            variant="flat"
            radius="full"
            aria-label={t('ideas.back')}
            onPress={goBack}
            className="bg-content1"
          >
            <BackIcon />
          </Button>
          <h1 className="flex-1 pr-10 text-center text-base font-bold">{t('ideas.title')}</h1>
        </header>

        {loading && !data ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : !data ? (
          <p role="alert" className="rounded-lg bg-content1 p-4 text-sm text-foreground/60">
            {error ? t('ideas.loadFailed') : null}
          </p>
        ) : (
          <>
            {/* The hero: what this is, how many ideas are in, how long is left. */}
            <motion.section
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative overflow-hidden rounded-xl bg-foreground p-5 text-background shadow-e2"
            >
              <Holds />
              <div className="relative flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[var(--gym-primary)]">
                  <TrophyIcon className="h-4 w-4" />
                  {open ? t('ideas.title') : t('ideas.winnersKicker')}
                </span>
                {open && (
                  <span className="rounded-full bg-[var(--gym-primary)] px-2.5 py-0.5 text-[11px] font-bold text-white">
                    {left === 0 ? t('ideas.lastDay') : t('ideas.daysLeft', { count: left })}
                  </span>
                )}
              </div>
              <h2 className="relative mb-4 mt-3 max-w-[14ch] text-balance text-2xl font-extrabold leading-[1.08] tracking-tight">
                {open ? t('ideas.heroTitle') : t('ideas.winnersTitle')}
              </h2>
              <div className="relative flex items-baseline gap-2">
                <AnimatedNumber
                  value={data.count}
                  className="text-[40px] font-extrabold leading-none tracking-tight text-[var(--gym-primary)] tabular-nums"
                />
                <span className="text-[13px] leading-tight text-background/65">
                  {t('ideas.countLabel', { count: data.count })}
                  <br />
                  {open ? t('ideas.countWhere') : t('ideas.countWhereClosed')}
                </span>
              </div>
              <div className="relative mt-5">
                <div className="h-1 overflow-hidden rounded-full bg-background/20">
                  <motion.div
                    className="h-full rounded-full bg-[var(--gym-primary)]"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.max(3, progress * 100)}%` }}
                    transition={{
                      duration: reduced ? 0 : 1.1,
                      ease: [0.16, 1, 0.3, 1],
                      delay: 0.2,
                    }}
                  />
                </div>
                <div className="mt-2 grid grid-cols-3 text-[11px] text-background/55">
                  <span>
                    <b className="block text-background">{t('ideas.markStartDate')}</b>
                    {open ? t('ideas.markStart') : t('ideas.markStartClosed')}
                  </span>
                  <span className="text-center">
                    <b className="block text-background">{t('ideas.markPickDate')}</b>
                    {open ? t('ideas.markPick') : t('ideas.markPickClosed')}
                  </span>
                  <span className="text-right">
                    <b className="block text-background">{t('ideas.markBuildDate')}</b>
                    {open ? t('ideas.markBuild') : t('ideas.markBuildClosed')}
                  </span>
                </div>
              </div>
            </motion.section>

            {/* Writing: starter chips give a first sentence; the field opens the sheet. */}
            <section className="flex flex-col gap-2.5">
              {open && (
                <div
                  className="-mx-4 flex gap-1.5 overflow-x-auto px-4 [scrollbar-width:none]"
                  aria-label={t('ideas.sparksLabel')}
                >
                  {SPARKS.map((key) => (
                    <Button
                      key={key}
                      size="sm"
                      radius="full"
                      variant="flat"
                      onPress={() => setComposer({ open: true, text: t(`ideas.spark.${key}Text`) })}
                      className="shrink-0 border border-[var(--gym-line)] bg-content1 font-semibold"
                    >
                      {t(`ideas.spark.${key}`)}
                    </Button>
                  ))}
                </div>
              )}
              <Button
                radius="full"
                variant="flat"
                onPress={() => setComposer({ open: true, text: '' })}
                className="h-14 w-full justify-between gap-3 border border-[var(--gym-line)] bg-content1 pl-5 pr-2 text-left text-[15px] font-normal text-foreground/55 shadow-e1"
                endContent={
                  <span className="flex aspect-square h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--gym-primary)] text-white">
                    <SendIcon />
                  </span>
                }
              >
                <span className="min-w-0 flex-1 truncate">
                  {open ? t('ideas.fieldIdea') : t('ideas.fieldFeedback')}
                </span>
              </Button>
            </section>

            {open ? (
              <ol className="flex flex-col gap-2.5">
                {([1, 2, 3] as const).map((n) => (
                  <li
                    key={n}
                    className="grid grid-cols-[24px_1fr] items-start gap-3 text-[13px] text-foreground/55"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-content1 text-[11px] font-bold text-foreground shadow-e1">
                      {n}
                    </span>
                    <span>
                      <b className="block text-sm text-foreground">{t(`ideas.how${n}Title`)}</b>
                      {t(`ideas.how${n}Text`)}
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              data.winners.length > 0 && (
                <section className="flex flex-col gap-2">
                  <h3 className="px-1 text-xs font-bold uppercase tracking-wider text-foreground/50">
                    {t('ideas.winnersSection')}
                  </h3>
                  {data.winners.map((w, i) => {
                    const yours = mineIdeaIds.has(w.id)
                    return (
                      <motion.div
                        key={w.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: reduced ? 0 : 0.1 + i * 0.07 }}
                        className={`flex items-center gap-3 rounded-lg border border-[var(--gym-line)] bg-content1 p-3 shadow-e1 ${
                          yours ? 'ring-2 ring-[var(--gym-primary)]' : ''
                        }`}
                      >
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-extrabold ${
                            i === 0 ? 'bg-[var(--gym-primary)] text-white' : 'bg-foreground/[0.07]'
                          }`}
                        >
                          {i + 1}
                        </span>
                        <span className="min-w-0 flex-1 text-sm">
                          <span className="block break-words font-semibold">{w.body}</span>
                          <span
                            className={`text-xs ${yours ? 'font-bold text-[var(--gym-primary)]' : 'text-foreground/50'}`}
                          >
                            {yours
                              ? t('ideas.yours')
                              : w.status === 'built'
                                ? t('ideas.winnerBuilt')
                                : t('ideas.winnerBuilding')}
                          </span>
                        </span>
                        <IdeaStatusChip kind="idea" status={w.status} />
                      </motion.div>
                    )
                  })}
                </section>
              )
            )}

            {/* Your own: the one place a member sees what happened to what they sent. */}
            <section ref={listRef} className="flex scroll-mt-4 flex-col gap-2">
              <h3 className="px-1 text-xs font-bold uppercase tracking-wider text-foreground/50">
                {t('ideas.mineTitle')}
              </h3>
              {data.mine.length === 0 ? (
                <div className="rounded-lg border-[1.5px] border-dashed border-[var(--gym-line)] bg-content1 px-4 py-5 text-center">
                  <span className="mx-auto mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--gym-primary)_14%,transparent)] text-[var(--gym-primary)]">
                    <KindIcon kind="idea" />
                  </span>
                  <b className="block text-sm">{t('ideas.mineEmptyTitle')}</b>
                  <span className="block text-balance text-[13px] text-foreground/55">
                    {t('ideas.mineEmptyText')}
                  </span>
                </div>
              ) : (
                data.mine.map((idea) => (
                  <motion.div
                    key={idea.id}
                    layout={!reduced}
                    initial={idea.id === freshId ? { opacity: 0, y: -14, scale: 0.97 } : false}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 26 }}
                  >
                    <Button
                      variant="flat"
                      radius="lg"
                      onPress={() => setDetailId(idea.id)}
                      className={`h-auto w-full justify-start gap-3 whitespace-normal border border-[var(--gym-line)] bg-content1 p-3 text-left shadow-e1 ${
                        idea.id === freshId ? 'ring-2 ring-[var(--gym-primary)]' : ''
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] ${
                          idea.kind === 'idea'
                            ? 'bg-[color-mix(in_srgb,var(--gym-primary)_14%,transparent)] text-[var(--gym-primary)]'
                            : 'bg-primary/10 text-primary-700'
                        }`}
                      >
                        <KindIcon kind={idea.kind} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 break-words text-[13px] font-medium leading-snug">
                          {idea.body}
                        </span>
                        <span className="text-[11px] text-foreground/45">
                          {idea.kind === 'idea' ? t('ideas.kindIdea') : t('ideas.kindFeedback')} ·{' '}
                          {formatDate(idea.createdAt)}
                        </span>
                      </span>
                      <IdeaStatusChip kind={idea.kind} status={idea.status} />
                    </Button>
                  </motion.div>
                ))
              )}
            </section>
          </>
        )}
      </div>

      <IdeaComposerSheet
        isOpen={composer.open}
        onClose={() => setComposer((c) => ({ ...c, open: false }))}
        campaignOpen={open}
        initialText={composer.text}
        onSend={handleSend}
      />
      <IdeaDetailSheet idea={detail} onClose={() => setDetailId(null)} formatDate={formatDate} />
    </div>
  )
}
